import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Button,
  Checkbox,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Row,
  Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { vendorApi } from '@/utils/vendorApi';
import { businessUnitLabel, useAuthStore } from '@/store/authStore';
import { ALL_BUSINESS_UNITS_ID, matchesSelectedBu } from '@/utils/businessUnits';
import { tagFilterParam, tagSelectOptions, useCatalogTags } from '@/utils/tagOptions';
import { ALL_VENDORS, useVendorIdOptions } from '@/utils/vendorIdOptions';
import { OnboardingStatus, VendorItem, VendorListParams } from '@/types';

const { Text } = Typography;

type StatusFilter = OnboardingStatus | 'all';

interface FilterDraft {
  vendorId: string;
  status: StatusFilter;
  includeArchived: boolean;
  tag: string;
}

const EMPTY_FILTERS: FilterDraft = {
  vendorId: ALL_VENDORS,
  status: 'all',
  includeArchived: false,
  tag: 'all',
};

const VENDOR_STATUS_VALUES: StatusFilter[] = ['all', 'pending', 'completed'];

function initialVendorFilters(): FilterDraft {
  const value = new URLSearchParams(window.location.search).get('status');
  const status = VENDOR_STATUS_VALUES.includes(value as StatusFilter)
    ? (value as StatusFilter)
    : 'all';
  return { ...EMPTY_FILTERS, status };
}

function toListParams(filters: FilterDraft, nextToken?: string): VendorListParams {
  return {
    vendorId: filters.vendorId.trim() || undefined,
    status: filters.status === 'all' ? undefined : filters.status,
    includeArchived: filters.includeArchived || undefined,
    tag: tagFilterParam(filters.tag),
    nextToken,
  };
}

function formatUpdatedAt(value?: string): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return '—';
  }
  return parsed.format('MMM DD, YYYY HH:mm');
}

const VendorList: React.FC = () => {
  const { message } = App.useApp();
  const { authorization, currentBusinessUnit } = useAuthStore();
  const tagChoices = useCatalogTags();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<FilterDraft>(initialVendorFilters);
  const [applied, setApplied] = useState<FilterDraft>(initialVendorFilters);
  const [vendors, setVendors] = useState<VendorItem[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const { vendorIdOptions, optionsLoading, optionRows } = useVendorIdOptions(
    'admin',
    draft.includeArchived,
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(
    async (filters: FilterDraft, append: boolean, cursor?: string) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await vendorApi.listVendors(toListParams(filters, cursor));
        setVendors((prev) => (append ? [...prev, ...response.vendors] : response.vendors));
        setNextToken(response.nextToken);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load vendors';
        message.error(errorMessage);
        if (!append) {
          setVendors([]);
          setNextToken(undefined);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [message]
  );

  useEffect(() => {
    void fetchPage(applied, false);
  }, [applied, fetchPage]);

  const applyFilters = () => {
    setVendors([]);
    setNextToken(undefined);
    setApplied({ ...draft });
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setVendors([]);
    setNextToken(undefined);
    setApplied(EMPTY_FILTERS);
  };

  const selectedId = currentBusinessUnit?.id;

  useEffect(() => {
    if (optionRows === null) {
      return;
    }
    const allowed = new Set(vendorIdOptions.map((option) => option.value));
    if (draft.vendorId && !allowed.has(draft.vendorId)) {
      setDraft((prev) => ({ ...prev, vendorId: ALL_VENDORS }));
    }
    if (applied.vendorId && !allowed.has(applied.vendorId)) {
      setApplied((prev) => ({ ...prev, vendorId: ALL_VENDORS }));
    }
  }, [optionRows, vendorIdOptions, draft.vendorId, applied.vendorId]);

  const visibleVendors = selectedId
    ? vendors.filter((row) => matchesSelectedBu(row.bu, selectedId, authorization?.catalog))
    : vendors;
  const filterNote = selectedId && selectedId !== ALL_BUSINESS_UNITS_ID && nextToken
    ? `Filtered to ${businessUnitLabel(selectedId)}. Load more to see additional results.`
    : null;

  const columns: ColumnsType<VendorItem> = [
    { title: 'Vendor ID', dataIndex: 'vendorId', key: 'vendorId', ellipsis: true },
    {
      title: 'BU',
      dataIndex: 'bu',
      key: 'bu',
      width: 140,
      render: (bu: string | null) => bu || '—',
    },
    {
      title: 'Tag',
      dataIndex: 'tag',
      key: 'tag',
      width: 140,
      render: (tag: string | null | undefined) => tag || '—',
    },
    { title: 'SFTP username', dataIndex: 'sftpUsername', key: 'sftpUsername', ellipsis: true },
    {
      title: 'Onboarding',
      dataIndex: 'onboardingStatus',
      key: 'onboardingStatus',
      width: 130,
      render: (status: string) => {
        const normalized = (status || '').toLowerCase();
        const color = normalized === 'completed' ? 'green' : normalized === 'pending' ? 'gold' : 'default';
        return <Tag color={color}>{status || '—'}</Tag>;
      },
    },
    {
      title: 'Lifecycle',
      dataIndex: 'lifecycleStatus',
      key: 'lifecycleStatus',
      width: 120,
      render: (lifecycleStatus: string | undefined) => (
        lifecycleStatus === 'archived' ? <Tag color="purple">Archived</Tag> : null
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (value: string | undefined) => formatUpdatedAt(value),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Filter by Vendor ID
        </Text>
        <Row gutter={12}>
          <Col xs={24} sm={8} md={6}>
            <Select
              style={{ width: '100%' }}
              showSearch
              loading={optionsLoading}
              value={draft.vendorId}
              onChange={(vendorId: string) => setDraft((prev) => ({ ...prev, vendorId }))}
              options={vendorIdOptions}
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              style={{ width: '100%' }}
              value={draft.tag}
              onChange={(tag: string) => setDraft((prev) => ({ ...prev, tag }))}
              options={tagSelectOptions(tagChoices)}
            />
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              style={{ width: '100%' }}
              value={draft.status}
              onChange={(status: StatusFilter) => setDraft((prev) => ({ ...prev, status }))}
              options={[
                { value: 'all', label: 'All' },
                { value: 'pending', label: 'pending' },
                { value: 'completed', label: 'completed' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Checkbox
              checked={draft.includeArchived}
              onChange={(event) => setDraft((prev) => ({
                ...prev,
                includeArchived: event.target.checked,
              }))}
            >
              Include archived
            </Checkbox>
          </Col>
          <Col xs={24} sm={24} md={4}>
            <Space>
              <Button type="primary" onClick={applyFilters}>
                Apply filters
              </Button>
              <Button onClick={clearFilters}>Clear</Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Table<VendorItem>
        columns={columns}
        dataSource={visibleVendors}
        rowKey={(row) => `${row.vendorId}::${row.hostname || 'default'}`}
        loading={loading}
        pagination={false}
        size="small"
        onRow={(record) => ({
          onClick: () => {
            const hostname = record.hostname || 'default';
            navigate(`/vendors/${encodeURIComponent(record.vendorId)}?hostname=${encodeURIComponent(hostname)}`);
          },
          style: { cursor: 'pointer' },
        })}
      />

      {nextToken && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {filterNote && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{filterNote}</Text>
          )}
          <Button onClick={() => void fetchPage(applied, true, nextToken)} loading={loadingMore}>
            Load more
          </Button>
        </div>
      )}
    </>
  );
};

export default VendorList;
